import { useEffect, useMemo, useRef, useState } from 'react';
import type { ScriptStatus, ScriptSummary } from '../../shared/types';
import { STATUS_LABELS } from '../../shared/types';
import Eyebrow from './Eyebrow';
import Button from './Button';

/** Local editor shape for one bundle item row — a superset of
 *  AdminBundleItemInput that also carries display info (resolved script
 *  title/status, warnings) and a stable React key. `key` is the server item
 *  id when loaded from the API, or a generated id for a row added
 *  client-side that hasn't been saved yet. Owned here since this file is
 *  the only thing that renders/reorders items; AdminBundleEditor imports it
 *  for its form state. */
export interface EditorItem {
  key: string;
  script_id: string | null;
  placeholder_title: string | null;
  show_before_ready: boolean;
  scriptInfo: { id: string; slug: string; title: string; status: ScriptStatus } | null;
  warning: string | null;
}

// ---------- Items editor ----------

export default function ItemsEditor({
  items,
  activeScripts,
  excludedScriptIds,
  clientNameById,
  dragIndex,
  onDragStart,
  onDrop,
  onDragEnd,
  onMove,
  onAddScript,
  onAddPlaceholder,
  onRemove,
  onToggleReady,
}: {
  items: EditorItem[];
  activeScripts: ScriptSummary[] | null;
  excludedScriptIds: Set<string>;
  clientNameById: Map<string, string>;
  dragIndex: number | null;
  onDragStart: (index: number) => void;
  onDrop: (index: number) => void;
  onDragEnd: () => void;
  onMove: (index: number, delta: number) => void;
  onAddScript: (script: ScriptSummary) => void;
  onAddPlaceholder: (title: string) => void;
  onRemove: (key: string) => void;
  onToggleReady: (key: string, showBeforeReady: boolean) => void;
}) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [placeholderOpen, setPlaceholderOpen] = useState(false);

  return (
    <div>
      <div className="flex flex-wrap md:flex-nowrap items-center justify-between gap-x-4 gap-y-2 md:gap-0 mb-3">
        <Eyebrow>Items · {items.length}</Eyebrow>
        <div className="flex items-center gap-2 relative">
          <Button
            variant="secondary"
            size="sm"
            className="whitespace-nowrap md:whitespace-normal"
            onClick={() => {
              setPickerOpen((v) => !v);
              setPlaceholderOpen(false);
            }}
          >
            + Add script
          </Button>
          <Button
            variant="secondary"
            size="sm"
            className="whitespace-nowrap md:whitespace-normal"
            onClick={() => {
              setPlaceholderOpen((v) => !v);
              setPickerOpen(false);
            }}
          >
            + Add placeholder
          </Button>
          {pickerOpen && (
            <AddScriptPicker
              scripts={activeScripts}
              excludedIds={excludedScriptIds}
              onPick={(s) => {
                onAddScript(s);
                setPickerOpen(false);
              }}
              onClose={() => setPickerOpen(false)}
            />
          )}
          {placeholderOpen && (
            <AddPlaceholderPopover
              onAdd={(title) => {
                onAddPlaceholder(title);
                setPlaceholderOpen(false);
              }}
              onClose={() => setPlaceholderOpen(false)}
            />
          )}
        </div>
      </div>

      {items.length === 0 ? (
        <div className="border border-dashed border-line rounded-md px-6 py-10 text-center bg-card">
          <p className="text-muted text-[14px]">
            No scripts yet. Add an existing script, or promise one by title
            with a placeholder.
          </p>
        </div>
      ) : (
        <div>
          {items.map((item, index) => (
            <ItemRow
              key={item.key}
              item={item}
              index={index}
              total={items.length}
              clientName={item.scriptInfo ? clientNameById.get(item.scriptInfo.id) : undefined}
              dragging={dragIndex === index}
              onDragStart={() => onDragStart(index)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                onDrop(index);
              }}
              onDragEnd={onDragEnd}
              onMoveUp={() => onMove(index, -1)}
              onMoveDown={() => onMove(index, 1)}
              onRemove={() => onRemove(item.key)}
              onToggleReady={(show) => onToggleReady(item.key, show)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ItemRow({
  item,
  index,
  total,
  clientName,
  dragging,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
  onMoveUp,
  onMoveDown,
  onRemove,
  onToggleReady,
}: {
  item: EditorItem;
  index: number;
  total: number;
  clientName: string | undefined;
  dragging: boolean;
  onDragStart: () => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
  onDragEnd: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onRemove: () => void;
  onToggleReady: (showBeforeReady: boolean) => void;
}) {
  const isPlaceholder = item.script_id === null;
  const isDraft = item.scriptInfo?.status === 'draft';
  const showReadyToggle = isPlaceholder || isDraft;
  const title = isPlaceholder ? item.placeholder_title : item.scriptInfo?.title ?? '(unknown script)';
  const meta = itemMeta(item, clientName);

  return (
    <div
      draggable
      onDragStart={(e) => {
        // Firefox won't actually start the drag unless dataTransfer carries
        // data — the value itself is unused since reordering is driven by
        // the dragIndex/onDragOver/onDrop state above, not by payload.
        e.dataTransfer.setData('text/plain', String(index));
        e.dataTransfer.effectAllowed = 'move';
        onDragStart();
      }}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
      className={`
        flex items-start gap-3 bg-card border rounded-sm px-4 py-3 mb-2
        transition-opacity duration-150
        ${dragging ? 'opacity-40 border-accent' : 'border-line'}
      `}
    >
      <span
        className="text-faint text-[15px] leading-[1.2] pt-0.5 cursor-grab select-none"
        aria-hidden
        title="Drag to reorder"
      >
        ⠿⠿
      </span>
      <div className="flex flex-col gap-1 pt-1">
        <button
          type="button"
          onClick={onMoveUp}
          disabled={index === 0}
          aria-label="Move up"
          title="Move up"
          className="text-faint hover:text-fg disabled:opacity-25 disabled:cursor-not-allowed leading-none text-[9px]"
        >
          ▲
        </button>
        <button
          type="button"
          onClick={onMoveDown}
          disabled={index === total - 1}
          aria-label="Move down"
          title="Move down"
          className="text-faint hover:text-fg disabled:opacity-25 disabled:cursor-not-allowed leading-none text-[9px]"
        >
          ▼
        </button>
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-medium text-[14px] text-fg truncate">{title}</div>
        {meta && <div className="text-[12px] text-faint mt-0.5">{meta}</div>}
        {item.warning && (
          <div className="text-[12px] text-warning mt-1 flex items-center gap-1">
            <span aria-hidden>⚠</span> {item.warning}
          </div>
        )}
        {showReadyToggle && (
          <div className="flex items-center gap-3 mt-2 flex-wrap">
            <span className="font-mono text-[10px] uppercase tracking-[0.1em] text-faint">
              Before it's ready
            </span>
            <button
              type="button"
              onClick={() => onToggleReady(true)}
              className={`text-[12px] whitespace-nowrap md:whitespace-normal ${item.show_before_ready ? 'text-accent font-semibold' : 'text-faint hover:text-muted'}`}
            >
              {item.show_before_ready ? '●' : '○'} Show as coming soon
            </button>
            <button
              type="button"
              onClick={() => onToggleReady(false)}
              className={`text-[12px] whitespace-nowrap md:whitespace-normal ${!item.show_before_ready ? 'text-accent font-semibold' : 'text-faint hover:text-muted'}`}
            >
              {!item.show_before_ready ? '●' : '○'} Keep hidden
            </button>
          </div>
        )}
      </div>
      <div className="flex items-center gap-2 flex-none pt-0.5">
        <ItemStatusPill item={item} />
        <button
          type="button"
          onClick={onRemove}
          aria-label="Remove item"
          title="Remove item"
          className="w-6 h-6 rounded-full flex items-center justify-center text-faint hover:text-accent hover:bg-accent/10 transition-colors"
        >
          ×
        </button>
      </div>
    </div>
  );
}

function itemMeta(item: EditorItem, clientName: string | undefined): string | null {
  if (item.script_id === null) return 'No script yet — a title-only promise';
  if (item.scriptInfo?.status === 'draft') {
    return clientName ? `${clientName} · Draft — not yet client-ready` : 'Draft — not yet client-ready';
  }
  return clientName ?? null;
}

function ItemStatusPill({ item }: { item: EditorItem }) {
  if (item.script_id === null) {
    return (
      <span className="shrink-0 font-mono text-[10px] uppercase font-medium tracking-[0.1em] px-2 py-1 rounded-full border border-line text-muted">
        Placeholder
      </span>
    );
  }
  const status = item.scriptInfo?.status;
  if (!status) return null;
  const cls =
    status === 'approved'
      ? 'border-accent bg-accent/10 text-accent'
      : status === 'draft'
        ? 'border-line text-muted'
        : 'border-accent/60 text-accent';
  return (
    <span className={`shrink-0 font-mono text-[10px] uppercase font-medium tracking-[0.1em] px-2 py-1 rounded-full border ${cls}`}>
      {STATUS_LABELS[status]}
    </span>
  );
}

// ---------- Add-script picker ----------

interface ClientGroup {
  name: string;
  scripts: ScriptSummary[];
}

function groupByClient(scripts: ScriptSummary[]): ClientGroup[] {
  const map = new Map<string, ScriptSummary[]>();
  for (const s of scripts) {
    const list = map.get(s.client_name);
    if (list) list.push(s);
    else map.set(s.client_name, [s]);
  }
  return Array.from(map.entries())
    .map(([name, list]) => ({ name, scripts: list }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

function AddScriptPicker({
  scripts,
  excludedIds,
  onPick,
  onClose,
}: {
  scripts: ScriptSummary[] | null;
  excludedIds: Set<string>;
  onPick: (script: ScriptSummary) => void;
  onClose: () => void;
}) {
  const [query, setQuery] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const available = (scripts ?? []).filter((s) => !excludedIds.has(s.id));
    const filtered = q
      ? available.filter(
          (s) => s.title.toLowerCase().includes(q) || s.client_name.toLowerCase().includes(q),
        )
      : available;
    return groupByClient(filtered);
  }, [scripts, excludedIds, query]);

  return (
    <div
      ref={ref}
      className="absolute z-30 top-[calc(100%+6px)] right-0 w-[300px] bg-card border border-line rounded-sm shadow-flare-lg/30 p-2 animate-rise-subtle"
    >
      <input
        autoFocus
        type="search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search scripts…"
        className="w-full mb-2 px-3 py-2 text-[13px] bg-page border border-line rounded focus:outline-none focus:border-accent transition-colors"
      />
      <div className="max-h-72 overflow-y-auto">
        {scripts === null ? (
          <p className="text-faint text-[12px] px-2 py-3">Loading…</p>
        ) : groups.length === 0 ? (
          <p className="text-faint text-[12px] px-2 py-3">No matching scripts.</p>
        ) : (
          groups.map((group) => (
            <div key={group.name}>
              <div className="font-mono text-[10px] uppercase tracking-[0.12em] text-faint px-2 pt-2 pb-1">
                {group.name}
              </div>
              {group.scripts.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => onPick(s)}
                  className="w-full text-left px-2 py-2 rounded flex items-center justify-between gap-2 hover:bg-elevated transition-colors"
                >
                  <span className="min-w-0 flex items-center gap-1.5">
                    <span className="text-[13px] font-medium text-fg truncate">{s.title}</span>
                    {s.template_key === 'interview_brief' && (
                      <span className="shrink-0 px-1.5 py-px rounded-full border border-line font-mono text-[10px] uppercase tracking-[0.1em] text-muted">
                        Interview brief
                      </span>
                    )}
                  </span>
                  <span className="text-[11px] text-faint shrink-0">{STATUS_LABELS[s.status]}</span>
                </button>
              ))}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function AddPlaceholderPopover({
  onAdd,
  onClose,
}: {
  onAdd: (title: string) => void;
  onClose: () => void;
}) {
  const [title, setTitle] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  function submit() {
    const trimmed = title.trim();
    if (!trimmed) return;
    onAdd(trimmed);
  }

  return (
    <div
      ref={ref}
      className="absolute z-30 top-[calc(100%+6px)] right-0 w-[280px] bg-card border border-line rounded-sm shadow-flare-lg/30 p-3 animate-rise-subtle"
    >
      <label className="block font-mono text-[10px] uppercase tracking-[0.12em] text-faint mb-1.5">
        Placeholder title
      </label>
      <input
        autoFocus
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') submit();
          if (e.key === 'Escape') onClose();
        }}
        placeholder="e.g. Behind the Scenes — 45s"
        maxLength={300}
        className="w-full px-3 py-2 text-[13px] bg-page border border-line rounded focus:outline-none focus:border-accent transition-colors mb-2"
      />
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onClose} className="text-[12px] text-muted hover:text-fg px-2 py-1">
          Cancel
        </button>
        <button
          type="button"
          disabled={!title.trim()}
          onClick={submit}
          className="text-[12px] font-medium text-accent hover:bg-accent/10 px-2 py-1 rounded disabled:opacity-40"
        >
          Add
        </button>
      </div>
    </div>
  );
}
